/**
 * NOTIFICATION PERMISSION PROMPT
 * 
 * A nice UI to ask users to enable push notifications
 * Can be shown as a banner, modal, or in settings
 * 
 * Location: src/components/NotificationPrompt.tsx
 */

import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { 
  isNotificationsSupported, 
  getNotificationPermission, 
  enablePushNotifications,
  disablePushNotifications
} from '../firebase/notifications';
import { getCurrentUser } from '../firebase/auth';

interface NotificationPromptProps {
  /** Show as a dismissible banner at top of page */
  variant?: 'banner' | 'card' | 'compact';
  /** Called when user dismisses the prompt */
  onDismiss?: () => void;
  /** Called after successful enable */
  onEnabled?: () => void;
}

export const NotificationPrompt: React.FC<NotificationPromptProps> = ({
  variant = 'card',
  onDismiss,
  onEnabled
}) => {
  const [isSupported, setIsSupported] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check notification support and current permission on mount
  useEffect(() => {
    const supported = isNotificationsSupported();
    setIsSupported(supported);
    
    if (supported) {
      setPermission(getNotificationPermission());
    }
  }, []);

  // Define handlers BEFORE any returns
  const handleEnable = async () => {
    const currentUser = getCurrentUser();
    
    if (!currentUser) {
      setError('Please sign in to enable notifications');
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await enablePushNotifications(currentUser.uid);

    setIsLoading(false);

    if (result.success) {
      setSuccess(true);
      setPermission('granted');
      onEnabled?.();
      
      // Auto-dismiss after success
      setTimeout(() => {
        onDismiss?.();
      }, 2000);
    } else {
      if (result.permission === 'denied') {
        setPermission('denied');
      }
      setError(result.error || 'Failed to enable notifications');
    }
  };

  const handleDisable = async () => {
    const currentUser = getCurrentUser();
    
    if (!currentUser) {
      setError('Please sign in to manage notifications');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await disablePushNotifications(currentUser.uid);
      // Note: We can't revoke browser permission, but we remove the token
      // so they won't receive notifications until they re-enable
      setPermission('default'); // Reset to show enable button
    } catch (err: any) {
      setError(err.message || 'Failed to disable notifications');
    }
    
    setIsLoading(false);
  };

  // Don't show if not supported
  if (!isSupported) {
    return null;
  }
  
  // If permission was denied, show message about browser settings
  if (permission === 'denied') {
    return (
      <div className={`
        ${variant === 'banner' ? 'w-full' : variant === 'compact' ? '' : 'max-w-md mx-auto'}
        bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3
      `}>
        <BellOff className="icon-base text-yellow-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-yellow-800 font-body">
            Notifications Blocked
          </p>
          <p className="text-sm text-yellow-600 font-body">
            To enable, click the lock icon in your browser's address bar and allow notifications.
          </p>
        </div>
      </div>
    );
  }

  // If already enabled, show status with option to disable
  if (permission === 'granted' && !success) {
    return (
      <div className={`
        ${variant === 'banner' ? 'w-full' : variant === 'compact' ? '' : 'max-w-md mx-auto'}
        bg-green-50 border border-green-200 rounded-xl p-4
      `}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bell className="icon-md text-green-600" />
            <div>
              <p className="font-medium text-green-800 font-body">
                Push Notifications Enabled ✓
              </p>
              <p className="text-sm text-green-600 font-body">
                You'll receive alerts for messages, wishlist matches, and destash inquiries.
              </p>
            </div>
          </div>
          <button
            onClick={handleDisable}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-gray-200 transition-default disabled:opacity-50 font-body"
          >
            {isLoading ? 'Disabling...' : 'Disable'}
          </button>
        </div>
        {error && (
          <p className="text-red-500 text-sm mt-2">{error}</p>
        )}
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className={`
        ${variant === 'banner' ? 'w-full' : 'max-w-md mx-auto'}
        bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3
      `}>
        <CheckCircle className="icon-base text-green-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-green-800 font-body">
            Notifications enabled! 🎉
          </p>
          <p className="text-sm text-green-600 font-body">
            You'll now receive updates about messages, wishlist items, and more.
          </p>
        </div>
      </div>
    );
  }

  // Compact variant (for settings page)
  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-between gap-4 p-4 bg-slate-800/60 rounded-xl border border-slate-600">
        <div className="flex items-center gap-3">
          <Bell className="icon-md text-glass" />
          <div>
            <p className="font-medium text-glass font-body">
              Push Notifications
            </p>
            <p className="text-sm text-glass-muted font-body">
              Get notified about messages and updates
            </p>
          </div>
        </div>
        <button
          onClick={handleEnable}
          disabled={isLoading}
          className="px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-800 transition-default disabled:opacity-50 flex items-center gap-2 font-body"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enabling...
            </>
          ) : (
            'Enable'
          )}
        </button>
      </div>
    );
  }

  // Banner variant (top of page)
  if (variant === 'banner') {
    return (
      <div className="w-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bell className="icon-md flex-shrink-0" />
            <p className="text-sm font-medium font-body">
              Enable notifications to stay updated on messages and wishlist items!
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEnable}
              disabled={isLoading}
              className="px-4 py-1.5 bg-white text-glass-secondary rounded-full font-bold text-sm hover:bg-slate-800/40 transition-default disabled:opacity-50 flex items-center gap-2 font-body"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Enable'
              )}
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-default"
                aria-label="Dismiss"
              >
                <X className="icon-md" />
              </button>
            )}
          </div>
        </div>
        {error && (
          <p className="text-center text-sm text-white/80 mt-2 font-body">
            {error}
          </p>
        )}
      </div>
    );
  }

  // Card variant (default)
  return (
    <div className="max-w-md mx-auto bg-slate-800/60 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-600 p-6">
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-glass-muted hover:text-glass-secondary transition-default"
          aria-label="Dismiss"
        >
          <X className="icon-md" />
        </button>
      )}
      
      <div className="text-center">
        <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Bell className="icon-lg text-glass" />
        </div>
        
        <h3 
          className="text-xl font-bold text-glass mb-2 font-heading"
        >
          Stay in the Loop! 🔔
        </h3>
        
        <p 
          className="text-glass-secondary mb-6 font-body"
        >
          Enable notifications to get instant updates when:
        </p>
        
        <ul className="text-left space-y-2 mb-6 font-body">
          <li className="flex items-center gap-2 text-glass-secondary">
            <span className="text-glass">💬</span>
            Someone sends you a message
          </li>
          <li className="flex items-center gap-2 text-glass-secondary">
            <span className="text-glass">✨</span>
            A wishlist item becomes available
          </li>
          <li className="flex items-center gap-2 text-glass-secondary">
            <span className="text-glass">🛒</span>
            Someone's interested in your destash
          </li>
          <li className="flex items-center gap-2 text-glass-secondary">
            <span className="text-glass">👤</span>
            You get a new follower
          </li>
        </ul>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="icon-md flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3">
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="flex-1 py-3 border-2 border-slate-600 text-glass-secondary rounded-xl font-medium hover:bg-slate-700 transition-default font-body"
            >
              Maybe Later
            </button>
          )}
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className={`${onDismiss ? 'flex-1' : 'w-full'} py-3 bg-slate-700 text-white rounded-xl font-bold hover:bg-slate-800 transition-default disabled:opacity-50 flex items-center justify-center gap-2 font-body`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enabling...
              </>
            ) : (
              <>
                <Bell className="icon-md" />
                Enable Notifications
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook to manage notification permission state
 */
export const useNotificationPermission = () => {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    const supported = isNotificationsSupported();
    setIsSupported(supported);
    
    if (supported) {
      setPermission(getNotificationPermission());
    } else {
      setPermission('unsupported');
    }
  }, []);

  return { permission, isSupported };
};

export default NotificationPrompt;
